import { useState, ReactNode, FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

// common
import { TabsDataContext, type TabsDataContextType } from 'modules/context';
import useGeneralHook from 'common/hook/useGeneralHook';
import useUserRoles from 'common/hook/useUserRoles';
import { ROUTES_PATH } from 'layout/constants';

// screen
import type {
  GovAgencyItems,
  CurrentSelectedRole,
  RolesToAssign,
  Unit,
} from 'modules/account/types';
import useGetAccountInfo, {
  defaultUserDetail,
  type UserDetail,
} from 'modules/account/services/useGetAccountInfo';

// helpers & hooks
import { convertOrgType } from 'modules/account/utils';
import { formatObject } from 'modules/account/detail/helpers/utils';
import { useAccountInfoSync } from 'modules/account/detail/hooks/useAccountInfoSync';
import { useRolesSync } from 'modules/account/detail/hooks/useRolesSync';
import { useAccountDetailUpdate } from 'modules/account/detail/hooks/useAccountDetailUpdate';
import { useAccountDetailNavGuard } from 'modules/account/detail/hooks/useAccountDetailNavGuard';

import useGetAccountRoles from 'modules/account/services/useGetAccountRoles';
import useGetListGovAgencies from 'modules/account/services/useGetListGovAgencies';
import useGetAvailableRoles from 'modules/account/services/useGetRoles';
import useGetHvsInfo from 'modules/account/services/useGetHvsInfo';
import { type Password, defaultPassword } from 'modules/account/services/useUpdatePassword';
import { defaultRolesToAssign } from 'modules/account/services/useCreateAccount';

interface TabsDataProviderProps {
  children: ReactNode;
  viewOnly?: boolean;
}

export const TabsDataProvider: FC<TabsDataProviderProps> = (props) => {
  const { children, viewOnly = false } = props;

  const [userInfo, setUserInfo] = useState<UserDetail>(defaultUserDetail);
  const [password, setPasswordState] = useState<Password>(defaultPassword);
  const [selectedRole, setSelectedRole] = useState<CurrentSelectedRole>();
  const [rolesToAssign, setRolesToAssign] = useState<RolesToAssign>(defaultRolesToAssign);
  const [unitAddress, setUnitAddress] = useState<Unit>();

  const navigate = useNavigate();
  const urlParams = useParams();
  const { pathname } = useGeneralHook();
  const { isNutriBAD, isSAD, isCSBAD } = useUserRoles();

  const { data: accountData } = useGetAccountInfo();
  const { data: govAgencies } = useGetListGovAgencies();
  const { data: availableRoles } = useGetAvailableRoles();
  const accountId = accountData?.id ?? urlParams.id ?? userInfo.id ?? '';
  const { data: accountRoles } = useGetAccountRoles(accountId);
  const { data: hvsInfo } = useGetHvsInfo();

  const toPreviousPage = () => {
    navigate(['', ROUTES_PATH.ad.index, ROUTES_PATH.account.index].join('/'));
  };

  const { handleUpdate, isSaving } = useAccountDetailUpdate({
    accountId,
    userInfo,
    rolesToAssign,
    password,
    toPreviousPage,
  });

  const setUserData = (fieldName: string, value: unknown) => {
    setUserInfo((old) => {
      if (!fieldName.includes('.')) return { ...old, [fieldName]: value };
      const [outerField, innerField] = fieldName.split('.');
      return { ...old, [outerField]: { ...(old[outerField] as object), [innerField]: value } };
    });
  };

  const isCQQL = useMemo(() => {
    if (!userInfo.userAuthen) return false;
    return convertOrgType(userInfo.userAuthen.userActualManagingOrgType) === 'CQQL';
  }, [userInfo.userAuthen]);

  const accountGovRoles: GovAgencyItems = useMemo(() => {
    if (!accountRoles) return [];
    return (accountRoles.govAgencyBusinessRoles ?? []).map((role) => ({
      ...formatObject(role.govAgencyBusinessRoleId, role.targetOrgId),
      includedClientIds: role.includedClientIds,
      includedClientByBoundary: role.includedClientByBoundary,
    })) as GovAgencyItems;
  }, [accountRoles]);

  useAccountInfoSync({
    accountData,
    govAgencies,
    allClients: availableRoles?.allClients,
    hvsInfo,
    setUserInfo,
    setUnitAddress,
  });

  useRolesSync({
    accountRoles,
    availableRoles: availableRoles ?? undefined,
    userAuthen: accountData?.userAuthen ?? userInfo.userAuthen,
    setRolesToAssign,
  });

  useAccountDetailNavGuard({ isNutriBAD, isSAD, isCSBAD, pathname });

  const contextValue: TabsDataContextType = {
    userInfo,
    password,
    selectedRole,
    rolesToAssign,
    accountRoles: accountRoles ?? null,
    unitAddress,
    accountGovRoles,
    hvsInfo: hvsInfo && hvsInfo.company !== undefined ? hvsInfo : undefined,
    setUserData,
    setPassword: setPasswordState,
    setSelectedRole,
    setRolesToAssign,
    handleUpdate,
    isSaving,
    toPreviousPage,
    isCQQL,
    viewOnly,
  };

  return <TabsDataContext.Provider value={contextValue}>{children}</TabsDataContext.Provider>;
};
